/**
 * Resolves where the AI client sends agent requests.
 *
 * - Next dev / vinext: no config -> use the same-origin proxy route
 *   `/api/ai-agent` (app/api/ai-agent/route.ts), which forwards to the gateway.
 * - Static SPA on S3/CloudFront: there is no server route, so the SPA calls the
 *   deployed gateway directly. The endpoint is injected at runtime via
 *   `window.__SATI_CONFIG__.agentEndpoint` (see /config.js) or by the SPA entry
 *   setting `globalThis.__SATI_AGENT_ENDPOINT__` from a build-time env var.
 *
 * Injecting via /config.js means the same build can be pointed at dev/pilot
 * without rebuilding.
 */
declare global {
  // eslint-disable-next-line no-var
  var __SATI_AGENT_ENDPOINT__: string | undefined;
  interface Window {
    __SATI_CONFIG__?: {
      agentEndpoint?: string;
      appApiEndpoint?: string;
      patientSource?: "mock" | "imported";
      /** Sign-in (Cognito). Omit to disable; see src/services/auth.ts. */
      auth?: { region: string; clientId: string; loginRequired?: boolean };
    };
  }
}

const PROXY_ENDPOINT = "/api/ai-agent";

function configuredEndpoint(): string | undefined {
  if (typeof globalThis !== "undefined" && globalThis.__SATI_AGENT_ENDPOINT__) {
    return globalThis.__SATI_AGENT_ENDPOINT__;
  }
  if (typeof window !== "undefined" && window.__SATI_CONFIG__?.agentEndpoint) {
    return window.__SATI_CONFIG__.agentEndpoint;
  }
  return undefined;
}

export type AgentUrls = { invokeUrl: string; statusUrl: string };

export function getAgentUrls(): AgentUrls {
  const configured = configuredEndpoint();
  if (!configured) {
    // Same-origin proxy handles both POST (invoke) and GET (status).
    return { invokeUrl: PROXY_ENDPOINT, statusUrl: PROXY_ENDPOINT };
  }
  const base = configured.replace(/\/+$/, "");
  // Gateway exposes POST /agent and GET /agent/health.
  const statusUrl = base.endsWith("/agent") ? `${base}/health` : base;
  return { invokeUrl: base, statusUrl };
}
